import {isUrl} from '@packages/utilities/string-utils';
import {test} from '@packages/utilities/fp/utils';

export const DEFAULT_VIDEO_WIDTH = 1280;
export const DEFAULT_VIDEO_HEIGHT = 720;

/**
 * safely divide width by height to get aspect ratio
 * @param  {Number} width  pixel width
 * @param  {Number} height pixel height
 * @return {Number}        aspect ratio
 */
export const getAspectRatio = (
  width?: string | number,
  height?: string | number
) => {
  // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string'.
  const w = parseInt(width, 10);
  // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string'.
  const h = parseInt(height, 10);
  if (w && h) {
    return w / h;
  } else {
    return DEFAULT_VIDEO_WIDTH / DEFAULT_VIDEO_HEIGHT;
  }
};

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Matches a quoted title attribute without crossing its closing quote, so
// markup after the iframe (e.g. Vimeo's trailing player.js script tag) is
// never consumed by the replacement.
const TITLE_ATTRIBUTE_REGEX = /title=("[^"]*"|'[^']*')/i;

export const setIframeTitle = (html: string, title: string): string => {
  let updatedHtml = html;

  if (title) {
    if (TITLE_ATTRIBUTE_REGEX.test(html)) {
      updatedHtml = html.replace(
        TITLE_ATTRIBUTE_REGEX,
        `allowfullscreen title="${escapeHtmlAttribute(title)}"`
      );
    } else {
      updatedHtml = html.replace(
        '<iframe',
        `<iframe allowfullscreen title="${escapeHtmlAttribute(title)}"`
      );
    }
  }

  return updatedHtml;
};

export const VIDEO_SERVICES: {
  [key: string]: {
    siteName: string;
    idRegex: RegExp;
    idMatchGroup: number;
    startRegex?: RegExp;
    startMatchGroup?: number;
    langMatchGroup?: number;
    embedUrl: (
      id: string,
      startTime?: string,
      optionalParams?: string
    ) => string;
    pageUrl: (
      id: string,
      startTime?: string,
      optionalParams?: string
    ) => string;
    oembedUrl?: (videoUrl: string) => string;
  };
} = {
  youtube: {
    siteName: 'YouTube',
    idRegex:
      /(?:youtu.be\/|youtube.com.*?v=|youtube.com\/embed\/|youtube.com\/v\/|youtube.com|youtube-nocookie.com\/embed\/)([^?\s'"&]+)/,
    idMatchGroup: 1,
    startRegex: /(?:\?|&)(?:t|start)=([\dhms]+)/,
    startMatchGroup: 1,
    embedUrl: (id, optionalParams = '') =>
      'https://www.youtube.com/embed/' + id + optionalParams,
    // Note: YouTube's custom start requires using the youtu.be optionalParams = '' (or using &feature=youtu.be query param but that's untidy)
    pageUrl: (id, optionalParams = '') =>
      'https://youtube.com/watch?v=' + id + optionalParams.replace('?', '&'),
  },
  vimeo: {
    siteName: 'Vimeo',
    idRegex:
      /vimeo.com\/(?:channels|groups\/name\/videos|album\/[^/]+\/video|video)?\/?([^\s'"?#]+)/,
    idMatchGroup: 1,
    startRegex: /(?:#|&)t=([\dhms]+)/,
    startMatchGroup: 1,
    embedUrl: (id, startTime = '') =>
      'https://player.vimeo.com/video/' + id + startTime,
    pageUrl: (id, startTime = '') => 'https://vimeo.com/' + id + startTime,
    oembedUrl: (videoUrl) =>
      'https://vimeo.com/api/oembed.json?url=' + encodeURIComponent(videoUrl),
  },
  dailymotion: {
    siteName: 'Daily Motion',
    idRegex:
      /(?:dailymotion.com|dai.ly)\/(?:embed\/)?(?:video\/)?([^\s'"/_?]+)/,
    idMatchGroup: 1,
    startRegex: /(?:\?|&)start=([\dhms]+)/,
    startMatchGroup: 1,
    embedUrl: (id, startTime = '') =>
      'https://www.dailymotion.com/embed/video/' + id + startTime,
    pageUrl: (id, startTime = '') =>
      'https://www.dailymotion.com/video/' + id + startTime,
    oembedUrl: (videoUrl) =>
      'https://www.dailymotion.com/services/oembed?url=' +
      encodeURIComponent(videoUrl),
  },
  imgur: {
    siteName: 'imgur gallery',
    idRegex: /imgur.com\/(?:gallery|a)\/([^/ '"?&]+)/,
    idMatchGroup: 1,
    embedUrl: (id) => 'https://imgur.com/a/' + id + '/embed',
    pageUrl: (id) => 'https://imgur.com/gallery/' + id,
  },
  vine: {
    siteName: 'Vine',
    idRegex: /(?:www.|\/\/)vine.co\/v\/([^\/'"]+)/,
    idMatchGroup: 1,
    embedUrl: (id) => 'https://vine.co/v/' + id + '/embed/simple',
    pageUrl: (id) => 'https://vine.co/v/' + id,
  },
  kickstarter: {
    siteName: 'Kickstarter',
    idRegex: /kickstarter.com\/projects\/([^/]+\/[^/\s]+)/,
    idMatchGroup: 1,
    embedUrl: (id) =>
      'https://www.kickstarter.com/projects/' + id + '/widget/video.html',
    pageUrl: (id) => 'https://www.kickstarter.com/projects/' + id,
  },
  scribd: {
    siteName: 'Scribd',
    idRegex: /scribd.com\/(?:doc|embeds|mobile\/documents)\/([^/]+)/,
    idMatchGroup: 1,
    embedUrl: (id) => 'https://www.scribd.com/embeds/' + id + '/content',
    pageUrl: (id) => 'https://www.scribd.com/doc/' + id,
  },
  ted: {
    siteName: 'TED',
    idRegex:
      /ted.com\/talks\/((?:lang\/[^/]+\/)?[^"'?\s/.]+)(?:\/transcript)?.*?\??.*?(?:language=([^&\s]+).*)?/,
    idMatchGroup: 1,
    langMatchGroup: 2,
    embedUrl: (id) => 'https://embed-ssl.ted.com/talks/' + id,
    pageUrl: (id) => 'https://www.ted.com/talks/' + id,
  },
  cloudapp: {
    siteName: 'CloudApp',
    idRegex: /cl\.ly\/([^\/"'?&]+)/,
    idMatchGroup: 1,
    embedUrl: (id) => 'https://embed.cl.ly/' + id,
    pageUrl: (id) => 'https://cl.ly/' + id,
  },
};

const videoRegexes = Object.keys(VIDEO_SERVICES).map(
  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  (service) => VIDEO_SERVICES[service].idRegex
);

export const isVideoURL = (link: string) =>
  videoRegexes.some((regex) => {
    const isVideo = test(regex);
    return isVideo(link);
  });

/**
 * Some video services store start times as either minutes and seconds or just seconds
 * This will look at the time string passed in like 3m26s or 206s and return just the seconds ie: 206 in both cases
 * @param  {String} time the time param/string from the video service url
 * @return {String}      The amount of seconds
 */
function convertVideoTimeToSeconds(time: string) {
  const hourMatch = time.match(/([\d]+)h/);
  const minMatch = time.match(/([\d]+)m/);
  const secMatch = time.match(/([\d]+)s/);

  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const hour = (hourMatch && parseInt(hourMatch[1], 10)) || 0;
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const min = (minMatch && parseInt(minMatch[1], 10)) || 0;
  const sec = (() => {
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    const _sec = (secMatch && parseInt(secMatch[1], 10)) || 0;

    // If there is no scale assume the digits represent seconds
    if (!hour && !min && !_sec) {
      const digitMatch = time.match(/[\d]+/);
      return (digitMatch && parseInt(digitMatch[0], 10)) || 0;
    } else {
      return _sec;
    }
  })();

  // Return the total seconds
  return String(hour * 3600 + min * 60 + sec);
}

/**
 * Get video id for supported services
 * Note: We prefer non-embed.ly oembed services where possible, and prefer non-embed.ly iframe embeds where possible
 * @param {String} embedText the url, embed code, or video id to parse
 * @return {Object} a video object {site: <site name>, id: <video id>, start: <optional: start time in seconds>}
 */
export function getVideoId(embedText: string) {
  const video: {
    site: string;
    id: string;
    siteName?: string;
    start?: string;
    error?: string;
  } = {site: '', id: ''};

  for (const serviceKey in VIDEO_SERVICES) {
    const service = VIDEO_SERVICES[serviceKey];

    // Detect the video service
    if (service && service.idRegex.test(embedText)) {
      video.site = serviceKey;
      video.siteName = service.siteName;

      const idMatch = embedText.match(service.idRegex);
      const id = idMatch && idMatch[service.idMatchGroup];

      // Match the video id
      if (id) {
        // Note: specific to TED we consider the language part of the embed id
        // The language string will match the first group $1 only when parsing the embed url or html
        // in other cases it'll be a query param captured in $5
        if (
          serviceKey === 'ted' &&
          service.langMatchGroup &&
          idMatch &&
          idMatch[service.langMatchGroup]
        ) {
          video.id = `lang/${idMatch[service.langMatchGroup]}/${id}`;
        } else {
          video.id = id;
        }

        // If the service supports custom start time, grab that value
        const startMatch =
          service.startRegex && embedText.match(service.startRegex);
        if (
          startMatch &&
          service.startMatchGroup &&
          startMatch[service.startMatchGroup]
        ) {
          const startValue = startMatch[service.startMatchGroup];
          // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
          video.start = convertVideoTimeToSeconds(startValue);
        }
      }

      // Stop searching once we detect a service
      break;
    }
  }

  if (!video.id) {
    // If it's a url, it's likely the embed code for another service that we don't officially support
    if (isUrl(embedText)) {
      video.site = 'other';
    } else {
      video.error = 'error';
    }
  }
  return video;
}

export const forTestSuite = {
  convertVideoTimeToSeconds,
} as const;
